<template>
  <div>
    <div
      v-if="selectedLocation.isLoading"
      class="section"
    >
      <div class="loader" />
    </div>



    <div
      v-else
      class="section center"
    >
      <div class="container max-width-small-mobile">
        <h1 class="title title--2 primary half-spacing">
          Welcome!
        </h1>

        <p class="title title--3 regular dark-gray">
          Select an option below.
        </p>

        <p
          v-if="!session.route.merchantLocationId"
          class="message message--danger"
        >
          A valid location is not selected. Please try rescanning your QR code or ask the waitstaff for assistance.
        </p>

        <p
          v-else-if="merchant.enabledApps.length === 0"
          class="message message--danger"
        >
          No options are available for this merchant at this time. Please try again later.
        </p>

        <template v-else>
          <button
            v-if="merchant.features['pay-at-table']"
            class="button button--primary button--largest"
            type="button"
            @click="selectApp('pay-at-table')"
          >
            <img
              class="image-icon"
              src="/images/icons/credit-card.svg"
            >
            Make a Payment
          </button>

          <button
            v-if="merchant.features['order-ahead']"
            class="button button--primary button--largest"
            type="button"
            @click="selectApp('order-ahead')"
          >
            <img
              class="image-icon"
              src="/images/icons/take-out.svg"
            >
            Order for Take Out
          </button>

          <button
            v-if="merchant.features['order-at-table']"
            class="button button--primary button--largest"
            type="button"
            @click="selectApp('order-at-table')"
          >
            <img
              class="image-icon"
              src="/images/icons/table.svg"
            >
            Order at Table
          </button>

          <button
            v-if="merchant.features['read-only-menu']"
            class="button button--primary button--largest"
            type="button"
            @click="selectApp('read-only-menu')"
          >
            <img
              class="image-icon"
              src="/images/icons/menu.svg"
            >
            See Menu
          </button>
        </template>
      </div>
    </div>
  </div>
</template>



<script>
  import events from '@/services/events';
  import logger from '@/services/logger';
  import { mapGetters } from 'vuex';



  export default {
    name: 'WelcomePage',

    metaInfo() {
      return {
        title: 'Welcome'
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['session'])
    },

    created() {
      this.createdHandler();
    },

    methods: {
      autoLoadPayAtTableOrder() {
        let shouldRedirect = false;

        const {
          customQuery,
          merchantLocationId,
          posLocationId,
          sublocationToken,
          tableNumber,
          ticketNumber
        } = this.session.route;

        const route = {
          name: 'payAtTable',
          params: {
            merchantId: this.$route.params.merchantId
          },
          query: {}
        };

        if (sublocationToken) {
          route.query.st = sublocationToken;
          shouldRedirect = true;
        }

        else if (tableNumber && merchantLocationId) {
          route.name = 'orderAtTable';
          route.query.ml = merchantLocationId;
          route.query.tb = tableNumber;
          shouldRedirect = true;
        }

        else if (ticketNumber && merchantLocationId) {
          route.query.ml = merchantLocationId;
          route.query.tn = ticketNumber;
          shouldRedirect = true;
        }

        else if (ticketNumber && posLocationId) {
          route.query.pl = posLocationId;
          route.query.tn = ticketNumber;
          shouldRedirect = true;
        }

        else if (customQuery && merchantLocationId) {
          route.query.ml = merchantLocationId;
          route.query.cq = customQuery;
          shouldRedirect = true;
        }

        if (shouldRedirect) {
          if (!this.merchant.features['pay-at-table']) {
            return false;
          }

          this.$router.replace(route);
        }

        return shouldRedirect;
      },

      autoSelectApp() {
        const redirected = this.autoLoadPayAtTableOrder();

        if (redirected) {
          return;
        }

        if (this.merchant.enabledApps.length === 1) {
          this.selectApp(this.merchant.enabledApps[0], true);
        }
      },

      async createdHandler() {
        this.$store.dispatch('session/updateRouteParams', {
          override: true,
          route: this.$route
        });

        await this.$store.dispatch('locations/getLocationConditionally');

        this.autoSelectApp();
      },

      handleDisabledAppSelection(app) {
        logger.error(new Error(`${app} is not enabled for merchant`));

        events.emit('full-page-message', {
          title: 'Oops!',
          subtitle: 'This feature is not available.',
          description: 'We apologize for the inconvenience. Please try again later.'
        });
      },

      returnAppRoute(app) {
        const routes = {
          'order-ahead': {
            name: 'autoStartOrder',
            params: {
              merchantId: this.$route.params.merchantId
            },
            query: {
              ml: this.session.route.merchantLocationId,
              si: this.session.route.storeId,
              ft: 'pick-up'
            }
          },
          'order-at-table': {
            name: 'orderAtTable',
            params: {
              merchantId: this.$route.params.merchantId
            },
            query: {
              ml: this.session.route.merchantLocationId,
              tb: this.session.route.tableNumber
            }
          },
          'pay-at-table': {
            name: 'makeAPayment',
            params: {
              merchantId: this.$route.params.merchantId
            },
            query: {
              ml: this.session.route.merchantLocationId
            }
          },
          'read-only-menu': {
            name: 'readOnlyMenu',
            params: {
              merchantId: this.$route.params.merchantId
            },
            query: {
              ml: this.session.route.merchantLocationId,
              si: this.session.route.storeId
            }
          }
        };

        return routes[app];
      },

      selectApp(app, autoRedirect) {
        if (!this.merchant.features[app]) {
          this.handleDisabledAppSelection(app);
        }

        else {
          const route = this.returnAppRoute(app);

          if (autoRedirect) {
            this.$router.replace(route);
          }

          else {
            this.$router.push(route);
          }
        }
      }
    }
  };
</script>
